import React, { useEffect, useState } from "react";
import { hazardTone, speak } from "@/lib/audio";
import { ShieldAlert, Check, TriangleAlert } from "lucide-react";

const RELIABILITY_COLOR = {
  CONFIRMED: "#DC2626", CORROBORATED: "#F59E0B", REPORTED: "#EAB308",
  OUTDATED: "#6B7280", RESOLVED: "#10B981",
};

export function ReliabilityChip({ value }) {
  const c = RELIABILITY_COLOR[value] || "#9CA3AF";
  return (
    <span className="font-mono text-[9px] font-bold px-1.5 py-0.5" style={{ color: c, border: `1px solid ${c}`, background: `${c}18` }}>
      {value}
    </span>
  );
}

// High-visibility premise-hazard banner with audible tone + acknowledgment.
export default function HazardBanner({ summary, hazards = [], fireEmsStaging, onAck, ackLabel = "Acknowledge", announce = true, compact = false }) {
  const [acked, setAcked] = useState(false);
  useEffect(() => {
    if (announce && hazards.length) {
      hazardTone();
      speak(`Premise hazard alert. ${summary || "Hazard at location."}`, { rate: 1.05 });
    }
  }, []); // eslint-disable-line

  if (!hazards.length) return null;

  const doAck = async () => {
    try { await onAck(); setAcked(true); }
    catch { /* keep control enabled so the operator can retry */ }
  };

  return (
    <div data-testid="hazard-banner" className="border-2 border-[#DC2626] bg-[#1a0b0c] mb-3">
      <div className="flex items-center gap-2 px-3 py-2 bg-[#DC2626]">
        <TriangleAlert size={16} className="text-white animate-pulse" />
        <span className="font-head font-bold text-white text-sm tracking-tight">{summary || "PREMISE HAZARD"}</span>
        {onAck && (
          <button data-testid="hazard-ack-btn" disabled={acked}
            onClick={doAck}
            className="ml-auto flex items-center gap-1.5 px-3 py-1 text-xs font-head bg-white text-[#DC2626] hover:bg-gray-200 transition-colors disabled:opacity-60">
            <Check size={13} /> {acked ? "Acknowledged" : ackLabel}
          </button>
        )}
      </div>
      {!compact && (
        <div className="p-3 space-y-2">
          {fireEmsStaging && (
            <div className="text-[11px] font-mono text-[#F59E0B] border-l-2 border-[#F59E0B] pl-2">
              FIRE/EMS: {fireEmsStaging}
            </div>
          )}
          {hazards.map((h, i) => (
            <div key={i} data-testid={`hazard-item-${i}`} className="border-l-2 border-[#DC2626] pl-2">
              <div className="flex items-center gap-2 flex-wrap">
                <ShieldAlert size={13} className="text-[#DC2626]" />
                <span className="font-head font-bold text-sm text-[#F3F4F6]">{h.type}</span>
                <ReliabilityChip value={h.reliability} />
                {h.weapon_type && <span className="font-mono text-[10px] text-[#EF4444]">{h.weapon_type}</span>}
              </div>
              <div className="text-xs text-[#F3F4F6] mt-0.5">{h.detail}</div>
              {h.handling_instructions && <div className="text-[11px] text-[#F59E0B] mt-0.5">▸ {h.handling_instructions}</div>}
              <div className="font-mono text-[9px] text-[#9CA3AF] mt-0.5">
                {h.source ? `Source: ${h.source} · ` : ""}{h.reported_date ? `Reported ${h.reported_date} · ` : ""}
                {h.review_date ? `Review ${h.review_date}` : ""}{h.related_incident ? ` · Rel. ${h.related_incident}` : ""}
                {h.verification_status ? ` · ${h.verification_status}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
